import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { getInitials } from '../lib/utils';
import { Users, Plus, Trash2, UserPlus, Shield, Trophy, Swords, Pencil, Power } from 'lucide-react';
import api from '../lib/api';

const teamsAPI = {
  getAll: () => api.get('/teams'),
  create: (data) => api.post('/teams', data),
  getById: (teamId) => api.get(`/teams/${teamId}`),
  addMember: (teamId, data) => api.post(`/teams/${teamId}/members`, data),
  removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
  toggleMember: (teamId, userId) => api.put(`/teams/${teamId}/members/${userId}/toggle`),
  update: (teamId, data) => api.put(`/teams/${teamId}`, data),
  delete: (teamId) => api.delete(`/teams/${teamId}`),
};

export default function Teams() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '' });
  const [newMember, setNewMember] = useState({ name: '', email: '' });

  const fetchTeams = async () => {
    try {
      const res = await teamsAPI.getAll();
      setTeams(res.data || []);
    } catch {
      // no teams collection yet - will be created
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTeams(); }, []);

  const handleCreate = async () => {
    if (!newTeam.name.trim()) { toast.error('Team name required'); return; }
    try {
      await teamsAPI.create(newTeam);
      toast.success('Team created');
      setCreateOpen(false);
      setNewTeam({ name: '', description: '' });
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleAddMember = async () => {
    if (!selectedTeam || !newMember.name.trim()) { toast.error('Name required'); return; }
    try {
      await teamsAPI.addMember(selectedTeam.team_id, newMember);
      toast.success('Member added');
      setAddMemberOpen(false);
      setNewMember({ name: '', email: '' });
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (teamId, userId) => {
    try {
      await teamsAPI.removeMember(teamId, userId);
      toast.success('Member removed');
      fetchTeams();
    } catch { toast.error('Failed'); }
  };

  const handleToggleMember = async (teamId, userId) => {
    try {
      await teamsAPI.toggleMember(teamId, userId);
      toast.success('Member toggled');
      fetchTeams();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Delete this team?')) return;
    try {
      await teamsAPI.delete(teamId);
      toast.success('Team deleted');
      if (selectedTeam?.team_id === teamId) setSelectedTeam(null);
      fetchTeams();
    } catch { toast.error('Failed'); }
  };

  const isTeamAdmin = (team) => team.admin_id === user?.user_id || user?.role === 'platform_admin';

  if (loading) {
    return (<Layout><div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{[1,2,3].map(i => <Skeleton key={i} className="h-48" />)}</div></div></Layout>);
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="teams-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">Teams</h1>
            <p className="text-muted-foreground text-sm mt-1">{teams.length} teams</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary hover:bg-secondary/90" data-testid="create-team-btn"><Plus className="mr-2 h-4 w-4" />Create Team</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Team</DialogTitle><DialogDescription>Create a new team and manage members</DialogDescription></DialogHeader>
              <div className="space-y-3 py-3">
                <div><Label>Team Name *</Label><Input placeholder="e.g., Smash Bros" value={newTeam.name} onChange={e => setNewTeam({...newTeam, name: e.target.value})} data-testid="new-team-name" /></div>
                <div><Label>Description</Label><Input placeholder="Optional description" value={newTeam.description} onChange={e => setNewTeam({...newTeam, description: e.target.value})} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} className="bg-secondary hover:bg-secondary/90" data-testid="submit-team-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Teams list */}
          <div className="lg:col-span-1 space-y-3">
            {teams.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" /><p className="text-muted-foreground">No teams yet</p></CardContent></Card>
            ) : teams.map(team => (
              <Card key={team.team_id} className={`cursor-pointer transition-all hover:border-primary/50 ${selectedTeam?.team_id === team.team_id ? 'border-primary ring-1 ring-primary/20' : ''}`} onClick={() => setSelectedTeam(team)} data-testid={`team-card-${team.team_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-secondary/10 flex items-center justify-center"><Shield className="h-5 w-5 text-secondary" /></div>
                      <div>
                        <div className="font-medium">{team.name}</div>
                        <div className="text-xs text-muted-foreground">{team.members?.length || 0} members</div>
                      </div>
                    </div>
                    {isTeamAdmin(team) && (
                      <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteTeam(team.team_id); }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Team Detail */}
          <div className="lg:col-span-2">
            {selectedTeam ? (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading uppercase tracking-tight">{selectedTeam.name}</CardTitle>
                    {isTeamAdmin(selectedTeam) && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/matches/new?team=${selectedTeam.team_id}`)} data-testid="team-create-match"><Swords className="mr-1 h-3.5 w-3.5" />Create Match</Button>
                        <Button size="sm" variant="outline" onClick={() => navigate(`/tournaments?create=true&team=${selectedTeam.team_id}`)} data-testid="team-create-tournament"><Trophy className="mr-1 h-3.5 w-3.5" />Create Tournament</Button>
                        <Button size="sm" onClick={() => { setAddMemberOpen(true); }} data-testid="team-add-member"><UserPlus className="mr-1 h-3.5 w-3.5" />Add Member</Button>
                      </div>
                    )}
                  </div>
                  {selectedTeam.description && <p className="text-sm text-muted-foreground mt-1">{selectedTeam.description}</p>}
                </CardHeader>
                <CardContent className="space-y-3">
                  {(!selectedTeam.members || selectedTeam.members.length === 0) ? (
                    <p className="text-muted-foreground text-sm text-center py-6">No members yet. Add members to get started.</p>
                  ) : selectedTeam.members.map((member, idx) => (
                    <div key={member.user_id || idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 group">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9"><AvatarFallback className="text-xs">{getInitials(member.name)}</AvatarFallback></Avatar>
                        <div>
                          <div className="font-medium text-sm">{member.name}</div>
                          <div className="text-xs text-muted-foreground">{member.email || ''} {member.role === 'admin' && <Badge variant="outline" className="ml-1 text-[10px]">Admin</Badge>}</div>
                        </div>
                      </div>
                      {isTeamAdmin(selectedTeam) && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button size="icon" variant="ghost" onClick={() => handleToggleMember(selectedTeam.team_id, member.user_id)} title={member.status === 'disabled' ? 'Enable' : 'Disable'}><Power className={`h-4 w-4 ${member.status === 'disabled' ? 'text-muted-foreground' : 'text-green-500'}`} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => handleRemoveMember(selectedTeam.team_id, member.user_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ) : (
              <Card><CardContent className="py-16 text-center"><Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" /><p className="text-muted-foreground">Select a team to view details</p></CardContent></Card>
            )}
          </div>
        </div>

        {/* Add Member Dialog */}
        <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Member</DialogTitle></DialogHeader>
            <div className="space-y-3 py-3">
              <div><Label>Name *</Label><Input placeholder="Member name" value={newMember.name} onChange={e => setNewMember({...newMember, name: e.target.value})} data-testid="member-name-input" /></div>
              <div><Label>Email</Label><Input placeholder="Email (optional)" value={newMember.email} onChange={e => setNewMember({...newMember, email: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddMemberOpen(false)}>Cancel</Button>
              <Button onClick={handleAddMember} className="bg-secondary hover:bg-secondary/90" data-testid="confirm-add-member">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
